import {Link, useLoaderData} from "react-router-dom";
import UserAccount from "../../../../Components/UserAccount";
import {Table} from "../../../../Components/Table";
import {ReactElement, useContext, useState} from "react";
import {useTranslation} from "react-i18next";
import {UserIsNotLogged} from "../../../../Components/UserUIMessages";
import {UserContext} from "../../../../Contexts/UserContext";
import {ColumnDef, getCoreRowModel, getPaginationRowModel, useReactTable} from "@tanstack/react-table";
import moment from "moment";
import {FormProvider, useForm} from "react-hook-form";
import {Filters, Search} from "./Filters";
import {ListOptions} from "./ListOptions";
import {Throbber} from "../../../../Components/Throbber";
import {SavedCartModal} from "../../SavedCarts/TransferSavedCartToCart";
import {useWZList} from "./useWZList";
import {WZDocumentType, WZListItem} from "./types";
import {DEFAULT_WZ_FILTER_VALUES} from "./filterDefaults";

const WZ_TYPE_TRANSLATION_KEYS: Record<WZDocumentType, string> = {
    return: 'user-page.wzs.types.return',
    sale: 'user-page.wzs.types.sale',
};

export default function WZs(): ReactElement {
    const initialWzs = useLoaderData() as WZListItem[] | null;
    const {t} = useTranslation();
    const userContext = useContext(UserContext);
    const {
        wzs,
        loading,
        requestError,
        downloadError,
        downloading,
        filterRequest,
        searchRequest,
        downloadRequest,
    } = useWZList(initialWzs);
    const [cartThrobber, setCartThrobber] = useState(false);
    const filterForm = useForm({defaultValues: DEFAULT_WZ_FILTER_VALUES});
    const searchForm = useForm({defaultValues: {'wz-number': ''}});

    const columns: ColumnDef<WZListItem>[] = [
        {
            header: t('user-page.wzs.date-create'),
            accessorKey: 'createdAt',
            cell: ({row}) => moment(row.original.createdAt).format('YYYY-MM-DD HH:mm'),
        },
        {
            header: t('user-page.wzs.type'),
            accessorKey: 'isReturn',
            cell: ({row}) => {
                return t(WZ_TYPE_TRANSLATION_KEYS[row.original.isReturn ? 'return' : 'sale']);
            },
        },
        {
            header: t('user-page.wzs.wz-number'),
            accessorKey: 'wzNumber',
        },
        {
            header: t('user-page.wzs.options'),
            accessorKey: 'id',
            cell: ({row}) => (
                <div className="options">
                    <div>
                        <Link to={'/user/wz/' + row.original.id}>
                            <button className="btn teal">
                                <i className="fas fa-file-invoice" /> {t('user-page.wzs.details')}
                            </button>
                        </Link>
                        <button
                            type="button"
                            className="btn teal"
                            disabled={downloading !== null}
                            onClick={() => void downloadRequest(row.original.id, 'xls')}
                        >
                            <i className="fas fa-file-excel" /> XLS
                        </button>
                        <button
                            type="button"
                            className="btn teal"
                            disabled={downloading !== null}
                            onClick={() => void downloadRequest(row.original.id, 'pdf')}
                        >
                            <i className="fas fa-file-pdf" /> PDF
                        </button>
                        <SavedCartModal
                            setThrobber={setCartThrobber}
                            endpoint="/user/wz/transform-to-cart"
                            idItem={row.original.id}
                        />
                    </div>

                </div>
            ),
        },
    ];

    const table = useReactTable({
        data: wzs,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    if (!userContext?.user) {
        return <UserIsNotLogged />;
    }

    if (initialWzs === null) {
        return <></>;
    }

    const breadcrumb = [{name: t('user-page.wzs.title'), path: null}];

    return (
        <UserAccount title={t('user-page.wzs.title')} className="my-wzs" breadcrumb={breadcrumb}>
            <div className="filters">
                <FormProvider {...searchForm}>
                    <Search onSubmit={searchRequest} />
                </FormProvider>
                <div className="or">{t('user-page.wzs.filters.or')}</div>
                <FormProvider {...filterForm}>
                    <Filters onSubmit={filterRequest} />
                </FormProvider>
            </div>
            <Throbber visible={loading || cartThrobber} />
            {requestError && (
                <div className="form-error">
                    <i className="fas fa-times-circle" />
                    <span>{t('user-page.wzs.request-error')}</span>
                </div>
            )}
            {downloadError && (
                <div className="form-error">
                    <i className="fas fa-times-circle" />
                    <span>{t('user-page.wzs.download-error')}</span>
                </div>
            )}
            {!loading && !requestError && wzs.length === 0 ? (
                <div className="empty">{t('user-page.wzs.empty-list')}</div>
            ) : null}
            {!loading && !requestError && wzs.length > 0 ? (
                <>
                    <ListOptions table={table} rowQuantity={wzs.length} />
                    <Table table={table} additionalBottom={undefined} />
                </>
            ) : null}
        </UserAccount>
    );
}
